import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './MonitGPS.css';
import { useLanguage } from './LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import T from './translations';

const ScrollReveal = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Elearning = () => {
  const { language } = useLanguage();
  const TT = T[language];
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 glass-effect"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <a href="/">
              <img src="/images/logominitgps.png" alt="MonitGPS Logo" className="h-12 w-auto cursor-pointer" />
            </a>
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="/" className="hover:text-cyan-400 transition-colors">{TT.nav.home}</a>
            <a href="/zawodnik" className="hover:text-cyan-400 transition-colors text-cyan-500 font-bold">{TT.nav.competitorPage}</a>
            <LanguageSwitcher />
          </div>
          <div className="md:hidden flex items-center space-x-3">
            <LanguageSwitcher />
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-cyan-400 focus:outline-none">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0a]/95 border-b border-white/10 flex flex-col px-6 py-4 space-y-4 text-sm font-medium backdrop-blur-xl">
            <a href="/" className="hover:text-cyan-400">{TT.nav.home}</a>
            <a href="/zawodnik" className="text-cyan-500 font-bold hover:text-cyan-400">{TT.nav.competitorPage}</a>
          </div>
        )}
      </motion.nav>

      {/* Hero */}
      <section className="relative pt-32 pb-16 flex items-center justify-center overflow-hidden bg-carbon min-h-[40vh]">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <motion.div
          className="absolute top-1/2 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.h1
            className="font-display font-black text-5xl md:text-6xl leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {TT.elearning.heroTitle1} <span className="text-gradient">{TT.elearning.heroTitle2}</span>
          </motion.h1>
          <motion.p
            className="text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {TT.elearning.heroSubtitle}
          </motion.p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 scan-line"></div>
      </section>

      {/* Content & Button */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-cyan-500/5 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-full bg-gradient-to-r from-blue-500/5 to-transparent"></div>

        <div className="max-w-4xl mx-auto">
          <ScrollReveal delay={0.2}>
            <div className="glass-effect rounded-[2rem] p-12 text-center relative overflow-hidden group border border-white/10">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="text-9xl font-display font-black">01</span>
              </div>
              <div className="relative z-10 space-y-8">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-xl shadow-cyan-500/20 mb-4 transform group-hover:scale-110 transition-transform duration-500">
                  <span className="text-5xl">⚡</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-display font-bold text-white tracking-tight">
                  {TT.elearning.readyTitle1} <span className="text-cyan-400">{TT.elearning.readyTitle2}</span>
                </h2>
                <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
                  {TT.elearning.subtitle}
                </p>
                <div className="pt-8">
                  <a
                    href="https://elearning.monitgps.pl"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center space-x-4 px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-display font-bold text-xl text-white hover:shadow-[0_0_40px_rgba(34,197,94,0.3)] hover:scale-[1.02] transition-all active:scale-95 group"
                  >
                    <span>{TT.elearning.launchBtn}</span>
                    <span className="text-2xl group-hover:translate-x-1 transition-transform">→</span>
                  </a>
                  <p className="mt-6 text-sm text-gray-500">
                    {TT.elearning.redirectNote} <span className="text-cyan-500/70">elearning.monitgps.pl</span>
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 glass-effect border-t border-white/5 mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between text-sm text-gray-500">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <p>{TT.footer.copyright}</p>
            <a href="/polityka-prywatnosci" className="hover:text-cyan-400 transition-colors underline decoration-white/20 underline-offset-4">
              {TT.footer.privacyPolicy}
            </a>
          </div>
          <div className="mt-4 md:mt-0">
            <p>{TT.footer.tagline}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Elearning;
